@if (mode === 'page') {
  <div class="rep-shell">
    <header class="rep-header">
      <div>
        <h1>Homeopathic Repertory</h1>
        <p class="rep-subtitle">Search rubrics and materia medica from your clinic database.</p>
      </div>
      <p-tag
        [severity]="status?.ready ? 'success' : 'warning'"
        [value]="loadingStatus ? 'Checking…' : (status?.ready ? 'Ready' : 'Not loaded')"
      />
    </header>

    @if (!loadingStatus && status && !status.ready) {
      <section class="rep-banner rep-banner--warn">
        <strong>{{ status.message }}</strong>
        <p>Run migrations and import OOREP data on the server, then refresh.</p>
      </section>
    }

    <ng-container *ngTemplateOutlet="workspaceBlock"></ng-container>
  </div>
} @else {
  @if (!loadingStatus && status && !status.ready) {
    <section class="rep-banner rep-banner--warn rep-banner--dialog">
      <strong>{{ status.message }}</strong>
      <p>Repertory data is not available on the server yet.</p>
    </section>
  }

  <ng-container *ngTemplateOutlet="workspaceBlock"></ng-container>
}

<ng-template #workspaceBlock>
  <div class="rep-workspace" [class.rep-workspace--page]="mode === 'page'" #repWorkspace>
    <div class="rep-workspace__panes">
      <aside
        class="rep-workspace__sidebar"
        [style.width.px]="mode === 'dialog' ? sidebarWidth : null"
        [style.flex]="mode === 'dialog' ? '0 0 ' + sidebarWidth + 'px' : null"
      >
      <div class="rep-sidebar__scroll">
      <div class="rep-sidebar__section">
        <div class="rep-sidebar__tabs">
          <button type="button" [class.active]="activeTab === 'repertory'" (click)="setActiveTab('repertory')">
            <i class="pi pi-list"></i>
            <span>Rubrics</span>
          </button>
          <button type="button" [class.active]="activeTab === 'mm'" (click)="setActiveTab('mm')">
            <i class="pi pi-book"></i>
            <span>MM</span>
          </button>
        </div>

        @if (targetLabel && mode !== 'dialog') {
          <div class="rep-sidebar__target">
            <i class="pi pi-map-marker"></i>
            <span>{{ targetLabel }}</span>
          </div>
        }
      </div>

      <section class="rep-search-card">
        <div class="rep-search-card__head">
          <div>
            <h4 class="rep-search-card__title">Symptom search</h4>
            @if (searchLanguageHint) {
              <p class="rep-search-card__hint">{{ searchLanguageHint }}</p>
            }
          </div>
          @if (languageBadge) {
            <span class="rep-search-card__lang">{{ languageBadge }}</span>
          }
        </div>

        <div class="rep-search-card__field">
          <i class="pi pi-search rep-search-card__icon"></i>
          <input
            id="rep-symptom-input"
            pInputText
            type="text"
            [(ngModel)]="symptom"
            [placeholder]="symptomPlaceholder"
            (keyup.enter)="search()"
          />
          @if (symptom) {
            <button type="button" class="rep-search-card__clear-input" (click)="symptom = ''" aria-label="Clear symptom">
              <i class="pi pi-times"></i>
            </button>
          }
        </div>

        <button
          type="button"
          class="rep-search-card__submit"
          [class.rep-search-card__submit--loading]="searching"
          [disabled]="searching || !symptom.trim()"
          (click)="search()"
        >
          @if (searching) {
            <i class="pi pi-spin pi-spinner"></i>
            <span>Searching…</span>
          } @else {
            <i class="pi pi-search"></i>
            <span>{{ activeTab === 'repertory' ? 'Search rubrics' : 'Search materia medica' }}</span>
          }
        </button>

        <div class="rep-search-card__quick">
          <span class="rep-search-card__quick-label">Try</span>
          <div class="rep-search-card__chips">
            @for (term of quickSearchTerms; track term.query) {
              <button
                type="button"
                class="rep-search-card__chip"
                [class.rep-search-card__chip--active]="isQuickTermActive(term)"
                (click)="quickSearch(term)"
              >
                {{ term.label }}
                @if (isGermanRepertorySelected && term.label !== term.query) {
                  <span class="rep-search-card__chip-de">{{ term.query }}</span>
                }
              </button>
            }
          </div>
        </div>
      </section>

      <section class="rep-filter-card">
        <button type="button" class="rep-filter-card__toggle" (click)="toggleAdvancedFilters()">
          <span class="rep-filter-card__toggle-left">
            <i class="pi pi-sliders-h"></i>
            <span>Search options</span>
            @if (activeFilterCount) {
              <em class="rep-filter-card__badge">{{ activeFilterCount }}</em>
            }
          </span>
          <i class="pi" [ngClass]="showAdvancedFilters ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
        </button>

        @if (showAdvancedFilters) {
          <div class="rep-filter-card__body">
            @if (activeTab === 'repertory') {
              <div class="rep-filter-field">
                <label>Repertory book</label>
                @if (repertories.length <= 3) {
                  <div class="rep-source-pills">
                    @for (source of repertories; track source.abbrev) {
                      <button
                        type="button"
                        class="rep-source-pill"
                        [class.rep-source-pill--active]="selectedRepertory === source.abbrev"
                        (click)="selectedRepertory = source.abbrev"
                      >
                        <span class="rep-source-pill__title">{{ source.title }}</span>
                        @if (source.language) {
                          <span class="rep-source-pill__lang">{{ source.language | uppercase }}</span>
                        }
                      </button>
                    }
                  </div>
                } @else {
                  <p-dropdown
                    [options]="repertories"
                    optionLabel="title"
                    optionValue="abbrev"
                    [(ngModel)]="selectedRepertory"
                    [filter]="true"
                    filterBy="title,abbrev"
                    styleClass="rep-dropdown"
                  />
                }
              </div>

              <div class="rep-filter-field">
                <label>Remedy grade filter</label>
                <p class="rep-filter-field__hint">Show only remedies at or above the selected grade in each rubric.</p>
                <div class="rep-grade-bar">
                  @for (option of weightOptions; track option.value) {
                    <button
                      type="button"
                      class="rep-grade-btn"
                      [class.rep-grade-btn--active]="minWeight === option.value"
                      (click)="setMinWeight(option.value)"
                    >
                      {{ option.label }}
                    </button>
                  }
                </div>
              </div>
            } @else {
              <div class="rep-filter-field">
                <label>Materia medica source</label>
                <p-dropdown
                  [options]="materiaMedicas"
                  optionLabel="title"
                  optionValue="abbrev"
                  [(ngModel)]="selectedMateriaMedica"
                  [autoDisplayFirst]="true"
                  [filter]="true"
                  filterBy="title,abbrev"
                  styleClass="rep-dropdown"
                />
              </div>
            }

            <div class="rep-filter-field">
              <label for="rep-remedy-filter">Limit to remedy</label>
              <div class="rep-remedy-filter">
                <i class="pi pi-filter"></i>
                <input
                  id="rep-remedy-filter"
                  pInputText
                  type="text"
                  [(ngModel)]="remedyFilter"
                  placeholder="Abbreviation, e.g. Bell."
                />
              </div>
            </div>
          </div>
        }
      </section>
      </div>

      <div class="rep-sidebar__footer">
      <div class="rep-sidebar__section rep-sidebar__meta">
        <div class="rep-sidebar__stat-grid">
          <div class="rep-sidebar__stat">
            <span>Active source</span>
            <strong>{{ activeSourceLabel }}</strong>
          </div>
          @if (pagination) {
            <div class="rep-sidebar__stat">
              <span>Matches</span>
              <strong>{{ pagination.total | number }}</strong>
            </div>
          }
          @if (activeTab === 'repertory' && minWeight > 0) {
            <div class="rep-sidebar__stat">
              <span>Grade filter</span>
              <strong>{{ minWeightLabel }}</strong>
            </div>
          }
        </div>
      </div>

      <div class="rep-sidebar__legend">
        <span class="rep-sidebar__legend-title">Grade key</span>
        <div class="rep-sidebar__legend-items">
          <span class="rep-legend rep-weight--1">1</span>
          <span class="rep-legend rep-weight--2">2</span>
          <span class="rep-legend rep-weight--3">3</span>
          <span class="rep-legend rep-weight--4">4</span>
        </div>
      </div>

      <button
        type="button"
        class="rep-sidebar__clear"
        [disabled]="!symptom && !hasSearched"
        (click)="clearSearch()"
      >
        <i class="pi pi-refresh"></i> Reset search
      </button>
      </div>
    </aside>

    @if (mode === 'dialog') {
      <div
        class="rep-workspace__splitter"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize search panel"
        title="Drag to resize panels"
        [class.is-dragging]="sidebarDragging"
        (mousedown)="startSidebarResize($event)"
      ></div>
    }

    <main class="rep-workspace__main">
      <div class="rep-main__toolbar">
        <div class="rep-main__toolbar-left">
          <h3>{{ activeTab === 'repertory' ? 'Matching rubrics' : 'Materia medica matches' }}</h3>
          @if (resultRangeLabel) {
            <span class="rep-main__range">{{ resultRangeLabel }}</span>
          }
        </div>
        @if (selectionMode) {
          <span class="rep-main__hint"><i class="pi pi-plus-circle"></i> Click a remedy to add it</span>
        }
      </div>

      <div class="rep-main__body" [class.rep-main__body--mm]="activeTab === 'mm'">
        @if (searching) {
          <div class="rep-main__skeletons">
            @for (i of [1, 2, 3, 4]; track i) {
              <div class="rep-skeleton"></div>
            }
          </div>
        } @else if (showInitialState) {
          <div class="rep-main__empty">
            <div class="rep-main__empty-icon"><i class="pi pi-compass"></i></div>
            <h4>Start with a symptom</h4>
            <p>Search in English (e.g. fever, headache) or pick a quick term. German Kent rubrics show an English gloss below each result.</p>
          </div>
        } @else if (showNoResults) {
          <div class="rep-main__empty">
            <div class="rep-main__empty-icon rep-main__empty-icon--muted"><i class="pi pi-inbox"></i></div>
            <h4>No matches</h4>
            <p>Try another keyword, change the source, or adjust the remedy filter.</p>
          </div>
        } @else if (activeTab === 'repertory') {
          <div class="rep-rubric-list">
            @for (rubric of rubricResults; track rubric.id) {
              <article class="rep-rubric" [class.rep-rubric--open]="isRubricExpanded(rubric.id)">
                <button type="button" class="rep-rubric__toggle" (click)="toggleRubric(rubric.id)">
                  <div class="rep-rubric__toggle-main">
                    <span class="rep-rubric__title">{{ rubric.text }}</span>
                    @if (rubricEnglishText(rubric); as englishText) {
                      <span class="rep-rubric__title-en">{{ englishText }}</span>
                    }
                    <div class="rep-rubric__crumbs">
                      @for (segment of pathSegments(rubric.full_path); track segment) {
                        <span>{{ segment }}</span>
                      }
                    </div>
                    @if (rubricEnglishPath(rubric); as englishPath) {
                      <div class="rep-rubric__crumbs rep-rubric__crumbs--en">
                        @for (segment of pathSegmentsEnglish(rubric.full_path, rubric); track segment) {
                          <span>{{ segment }}</span>
                        }
                      </div>
                    }
                  </div>
                  <div class="rep-rubric__toggle-meta">
                    <span class="rep-rubric__count">{{ rubric.remedies.length }}</span>
                    <i class="pi" [ngClass]="isRubricExpanded(rubric.id) ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
                  </div>
                </button>

                @if (isRubricExpanded(rubric.id)) {
                  @if (rubric.remedies.length) {
                    <div class="rep-remedy-grid">
                      @for (remedy of rubric.remedies; track remedy.id) {
                        <div
                          class="rep-remedy-item"
                          [class.rep-remedy-item--picker-open]="isPotencyPickerOpen(remedy.id)"
                        >
                          <button
                            type="button"
                            class="rep-remedy-row"
                            [ngClass]="weightClass(remedy.weight)"
                            [class.rep-remedy-row--selectable]="selectionMode"
                            [disabled]="!selectionMode"
                            (click)="selectRemedy(remedy, $event)"
                          >
                            <span class="rep-remedy-row__abbrev">{{ remedy.abbrev }}</span>
                            <span class="rep-remedy-row__name">{{ remedy.name }}</span>
                            <span
                              class="rep-remedy-row__clinic"
                              [class.rep-remedy-row__clinic--yes]="isClinicAvailable(remedy)"
                              [class.rep-remedy-row__clinic--no]="!isClinicAvailable(remedy)"
                              [pTooltip]="clinicAvailabilityTooltip(remedy)"
                              tooltipPosition="top"
                            >
                              {{ isClinicAvailable(remedy) ? 'In clinic' : 'Not in clinic' }}
                            </span>
                            <span class="rep-remedy-row__grade">{{ weightLabel(remedy.weight ?? 0) }}</span>
                            @if (selectionMode) {
                              <i class="pi pi-plus rep-remedy-row__add"></i>
                            }
                          </button>
                          @if (selectionMode && isPotencyPickerOpen(remedy.id) && remedy.clinic_matches?.length) {
                            <div class="rep-remedy-potencies">
                              <span class="rep-remedy-potencies__label">Choose potency</span>
                              @for (match of remedy.clinic_matches; track match.id) {
                                <button
                                  type="button"
                                  class="rep-remedy-potencies__btn"
                                  (click)="selectRemedyPotency(remedy, match, $event)"
                                >
                                  {{ match.potency }}
                                </button>
                              }
                            </div>
                          }
                        </div>
                      }
                    </div>
                  } @else {
                    <p class="rep-rubric__empty">No remedies matched your filters.</p>
                  }
                }
              </article>
            }
          </div>
        } @else {
          <div class="rep-mm-list">
            @for (section of mmResults; track section.id) {
              <article
                class="rep-mm-card"
                [class.rep-mm-card--picker-open]="isMmPotencyPickerOpen(section.id)"
              >
                <header class="rep-mm-card__header">
                  <div class="rep-mm-card__header-main">
                    @if (section.chapter_heading) {
                      <button
                        type="button"
                        class="rep-mm-card__remedy"
                        [class.rep-mm-card__remedy--selectable]="selectionMode"
                        [disabled]="!selectionMode"
                        (click)="selectRemedyFromMm(section, $event)"
                      >
                        <i class="pi pi-bookmark"></i>
                        <span>{{ section.chapter_heading }}</span>
                        @if (selectionMode) {
                          <i class="pi pi-plus"></i>
                        }
                      </button>
                    }
                    @if (selectionMode) {
                      <span
                        class="rep-mm-card__clinic"
                        [class.rep-mm-card__clinic--yes]="isMmClinicAvailable(section)"
                        [class.rep-mm-card__clinic--no]="!isMmClinicAvailable(section)"
                        [pTooltip]="mmClinicAvailabilityTooltip(section)"
                        tooltipPosition="top"
                      >
                        {{ isMmClinicAvailable(section) ? 'In clinic' : 'Not in clinic' }}
                      </span>
                    }
                  </div>
                  <span class="rep-mm-card__match" [class.rep-mm-card__match--heading]="section.match_in === 'heading'">
                    {{ mmMatchLabel(section) }}
                  </span>
                </header>
                @if (selectionMode && isMmPotencyPickerOpen(section.id) && section.clinic_matches?.length) {
                  <div class="rep-remedy-potencies rep-remedy-potencies--mm">
                    <span class="rep-remedy-potencies__label">Choose potency</span>
                    @for (match of section.clinic_matches; track match.id) {
                      <button
                        type="button"
                        class="rep-remedy-potencies__btn"
                        (click)="selectRemedyPotencyFromMm(section, match, $event)"
                      >
                        {{ match.potency }}
                      </button>
                    }
                  </div>
                }
                <h4 class="rep-mm-card__section">{{ section.heading }}</h4>
                @if (mmDisplayExcerpt(section); as excerpt) {
                  <p class="rep-mm-card__excerpt">{{ excerpt }}</p>
                }
              </article>
            }
          </div>
        }
      </div>

      @if (pagination && pagination.last_page > 1) {
        <footer class="rep-main__footer">
          <button
            type="button"
            class="rep-pager-btn"
            aria-label="Previous page"
            [disabled]="pagination.current_page <= 1 || searching"
            (click)="goToPage(pagination.current_page - 1)"
          >
            <i class="pi pi-angle-left"></i>
          </button>
          <span class="rep-pager-label">
            Page {{ pagination.current_page }} of {{ pagination.last_page }}
          </span>
          <button
            type="button"
            class="rep-pager-btn"
            aria-label="Next page"
            [disabled]="pagination.current_page >= pagination.last_page || searching"
            (click)="goToPage(pagination.current_page + 1)"
          >
            <i class="pi pi-angle-right"></i>
          </button>
        </footer>
      }
    </main>
    </div>

    @if (selectionMode && recentlyAdded.length) {
      <div class="rep-workspace__session">
        <span class="rep-workspace__session-label">
          <i class="pi pi-check-circle"></i> Added this session ({{ addedCount }})
        </span>
        <div class="rep-workspace__session-items">
          @for (name of recentlyAdded; track name) {
            <span class="rep-workspace__session-chip">{{ name }}</span>
          }
        </div>
      </div>
    }
  </div>
</ng-template>
